import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import { LIEN_POSITIONS /* PROPERTY_SUBTYPES, VALIDATION_RULES */ } from '../../utils/constants';
import { 
  formatCurrency, 
  calculateLTV, 
  validateLTVForLien, 
  // isPropertyEligibleForListing, // Unused import
  generateListingId,
  generateListingTitle
} from '../../utils/helpers';
import LtvBadge from '../molecules/LtvBadge';
import EncumbranceBadge from '../molecules/EncumbranceBadge';
import './listasset.css';

const ListAsset = () => {
  const history = useHistory();
  const { state, actions } = useDemo();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Property Selection
    propertyId: '',
    
    // Step 2: Loan Terms
    requestedAmount: '',
    desiredTermMonths: 12,
    desiredLien: 'FirstLien',
    
    // Step 3: Additional Details
    description: '',
    requirements: ['Standard financial reporting'],
    
    // Owner Information
    owner: {
      name: '',
      email: ''
    }
  });

  const [errors, setErrors] = useState({});

  const selectedProperty = state.properties.find(p => p.id === formData.propertyId);
  const ltv = selectedProperty && formData.requestedAmount ? 
    calculateLTV(parseFloat(formData.requestedAmount), selectedProperty.valuation) : 0;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleOwnerChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      owner: {
        ...prev.owner,
        [field]: value
      }
    }));
  };

  const handleRequirementChange = (index, value) => {
    const newRequirements = [...formData.requirements];
    newRequirements[index] = value;
    setFormData(prev => ({
      ...prev,
      requirements: newRequirements
    }));
  };

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};

    switch (stepNumber) {
      case 1:
        if (!formData.propertyId) {
          newErrors.propertyId = 'Please select a property';
        }
        break;
      case 2:
        if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) {
          newErrors.requestedAmount = 'Please enter a valid amount';
        }
        if (selectedProperty && formData.requestedAmount) {
          const amount = parseFloat(formData.requestedAmount);
          if (amount > selectedProperty.valuation) {
            newErrors.requestedAmount = 'Amount cannot exceed property valuation';
          }
          if (!validateLTVForLien(ltv, formData.desiredLien)) {
            newErrors.ltv = `LTV too high for ${formData.desiredLien} position`;
          }
        }
        break;
      case 3:
        if (!formData.description.trim()) {
          newErrors.description = 'Please provide a description';
        }
        if (!formData.owner.name.trim()) {
          newErrors.ownerName = 'Please enter owner name';
        }
        if (!formData.owner.email.trim()) {
          newErrors.ownerEmail = 'Please enter owner email';
        }
        break;
      default:
        // No validation needed for other steps
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      if (step < 3) {
        setStep(step + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    const listing = {
      id: generateListingId(),
      assetId: formData.propertyId,
      title: generateListingTitle(selectedProperty, parseFloat(formData.requestedAmount), formData.desiredTermMonths),
      description: formData.description,
      requestedAmount: parseFloat(formData.requestedAmount),
      desiredTermMonths: formData.desiredTermMonths,
      desiredLien: formData.desiredLien,
      status: 'Open',
      ltv: ltv,
      badges: ['Property', formData.desiredLien === 'FirstLien' ? '1st-Lien' : '2nd-Lien'],
      committedAmount: 0,
      investorCount: 0,
      location: selectedProperty.address.split(',').slice(-2).join(',').trim(),
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      owner: {
        name: formData.owner.name,
        email: formData.owner.email
      },
      requirements: formData.requirements.filter(req => req.trim())
    };

    actions.createListing(listing);
    actions.showToast('Property listing created successfully!', 'success');
    history.push('/jo-xyz-demo/marketplace');
  };

  const renderStep1 = () => (
    <div className="jo-xyz-demo-list-asset-step">
      <h3>Select Property</h3>
      <p>Choose the property you want to list for financing.</p>
      
      <div className="jo-xyz-demo-property-selection">
        {state.properties.map((property) => (
          <div 
            key={property.id}
            className={`jo-xyz-demo-property-option ${formData.propertyId === property.id ? 'jo-xyz-demo-property-option--selected' : ''}`}
            onClick={() => handleInputChange('propertyId', property.id)}
          >
            <div className="jo-xyz-demo-property-option__header">
              <div className="jo-xyz-demo-property-option__address">
                <h4>{property.address}</h4>
                <p>{property.subtype}</p>
              </div>
              <div className="jo-xyz-demo-property-option__valuation">
                {formatCurrency(property.valuation)}
              </div>
            </div>
            
            <div className="jo-xyz-demo-property-option__details">
              <div className="jo-xyz-demo-property-detail">
                <span className="jo-xyz-demo-property-detail__label">Cap Rate</span>
                <span className="jo-xyz-demo-property-detail__value">{(property.capRate * 100).toFixed(1)}%</span>
              </div>
              <div className="jo-xyz-demo-property-detail">
                <span className="jo-xyz-demo-property-detail__label">NOI</span>
                <span className="jo-xyz-demo-property-detail__value">{formatCurrency(property.noi)}</span>
              </div>
              <div className="jo-xyz-demo-property-detail">
                <span className="jo-xyz-demo-property-detail__label">Occupancy</span>
                <span className="jo-xyz-demo-property-detail__value">{(property.occupancyRate * 100).toFixed(0)}%</span>
              </div>
            </div>
            
            <div className="jo-xyz-demo-property-option__status">
              <EncumbranceBadge 
                lienPosition={property.lienPosition}
                encumbrances={property.encumbrances}
                inUnderwriting={property.inUnderwriting}
                size="small"
              />
              {!property.eligible && (
                <span className="jo-xyz-demo-status-badge jo-xyz-demo-status-badge--ineligible">Not Eligible</span>
              )}
            </div>
          </div>
        ))}
        
        {errors.propertyId && (
          <div className="jo-xyz-demo-error-message">{errors.propertyId}</div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="jo-xyz-demo-list-asset-step">
      <h3>Loan Terms</h3>
      <p>Define the financing terms for your property.</p>
      
      <div className="jo-xyz-demo-form-grid">
        <div className="jo-xyz-demo-form-group">
          <label>Requested Amount *</label>
          <input
            type="number"
            className="jo-xyz-demo-input"
            value={formData.requestedAmount}
            onChange={(e) => handleInputChange('requestedAmount', e.target.value)}
            placeholder="0"
            min="100000"
            step="10000"
          />
          {selectedProperty && (
            <div className="jo-xyz-demo-form-help">
              Max: {formatCurrency(selectedProperty.valuation)}
            </div>
          )}
          {errors.requestedAmount && (
            <div className="jo-xyz-demo-error-message">{errors.requestedAmount}</div>
          )}
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Term (Months) *</label>
          <select
            className="jo-xyz-demo-select jo-xyz-demo-input"
            value={formData.desiredTermMonths}
            onChange={(e) => handleInputChange('desiredTermMonths', parseInt(e.target.value))}
          >
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={18}>18 months</option>
            <option value={24}>24 months</option>
            <option value={36}>36 months</option>
            <option value={48}>48 months</option>
            <option value={60}>60 months</option>
          </select>
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Lien Position *</label>
          <select
            className="jo-xyz-demo-select jo-xyz-demo-input"
            value={formData.desiredLien}
            onChange={(e) => handleInputChange('desiredLien', e.target.value)}
          >
            {LIEN_POSITIONS.map(position => (
              <option key={position} value={position}>
                {position === 'FirstLien' ? 'First Lien' : 
                 position === 'SecondLien' ? 'Second Lien' : 
                 position}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* LTV Preview */}
      {selectedProperty && formData.requestedAmount && (
        <div className="jo-xyz-demo-ltv-preview">
          <div className="jo-xyz-demo-ltv-preview__header">
            <h4>Loan-to-Value Preview</h4>
          </div>
          <div className="jo-xyz-demo-ltv-preview__content">
            <div className="jo-xyz-demo-ltv-preview__badge">
              <LtvBadge ltv={ltv} size="medium" />
            </div>
            <div className="jo-xyz-demo-ltv-preview__details">
              <div className="jo-xyz-demo-ltv-detail">
                <span>Property Value:</span>
                <span>{formatCurrency(selectedProperty.valuation)}</span>
              </div>
              <div className="jo-xyz-demo-ltv-detail">
                <span>Requested Amount:</span>
                <span>{formatCurrency(formData.requestedAmount)}</span>
              </div>
              <div className="jo-xyz-demo-ltv-detail">
                <span>LTV:</span>
                <span>{(ltv * 100).toFixed(1)}%</span>
              </div>
            </div>
            {errors.ltv && (
              <div className="jo-xyz-demo-error-message">{errors.ltv}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="jo-xyz-demo-list-asset-step">
      <h3>Additional Details</h3>
      <p>Provide additional information about your listing.</p>
      
      <div className="jo-xyz-demo-form-grid">
        <div className="jo-xyz-demo-form-group jo-xyz-demo-form-group--full">
          <label>Listing Description *</label>
          <textarea
            className="jo-xyz-demo-input"
            rows="4"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the property, financing needs, and any special requirements..."
          />
          {errors.description && (
            <div className="jo-xyz-demo-error-message">{errors.description}</div>
          )}
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Owner Name *</label>
          <input
            type="text"
            className="jo-xyz-demo-input"
            value={formData.owner.name}
            onChange={(e) => handleOwnerChange('name', e.target.value)}
            placeholder="Property owner name"
          />
          {errors.ownerName && (
            <div className="jo-xyz-demo-error-message">{errors.ownerName}</div>
          )}
        </div>
        
        <div className="jo-xyz-demo-form-group">
          <label>Owner Email *</label>
          <input
            type="email"
            className="jo-xyz-demo-input"
            value={formData.owner.email}
            onChange={(e) => handleOwnerChange('email', e.target.value)}
            placeholder="owner@example.com"
          />
          {errors.ownerEmail && (
            <div className="jo-xyz-demo-error-message">{errors.ownerEmail}</div>
          )}
        </div>
      </div>
      
      <div className="jo-xyz-demo-requirements-section">
        <div className="jo-xyz-demo-requirements-header">
          <label>Requirements</label>
          <button 
            type="button"
            className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--sm"
            onClick={addRequirement}
          >
            Add Requirement
          </button>
        </div>
        <div className="jo-xyz-demo-requirements-list">
          {formData.requirements.map((requirement, index) => (
            <div key={index} className="jo-xyz-demo-requirement-item">
              <input
                type="text"
                className="jo-xyz-demo-input"
                value={requirement}
                onChange={(e) => handleRequirementChange(index, e.target.value)}
                placeholder="Enter requirement..."
              />
              <button 
                type="button"
                className="jo-xyz-demo-btn jo-xyz-demo-btn--danger jo-xyz-demo-btn--sm"
                onClick={() => removeRequirement(index)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="jo-xyz-demo-list-asset-page">
      <div className="jo-xyz-demo-list-asset-page__header">
        <h1>List Property for Financing</h1>
        <p className="jo-xyz-demo-list-asset-page__subtitle">
          Create a listing to attract investors for your commercial real estate
        </p>
      </div>

      <div className="jo-xyz-demo-list-asset-page__content">
        <div className="jo-xyz-demo-list-asset-container">
          {/* Progress Indicator */}
          <div className="jo-xyz-demo-list-asset-progress">
            <div className="progress-steps">
              {[1, 2, 3].map((stepNumber) => (
                <div 
                  key={stepNumber}
                  className={`jo-xyz-demo-progress-step ${step >= stepNumber ? 'jo-xyz-demo-progress-step--active' : ''} ${step > stepNumber ? 'jo-xyz-demo-progress-step--completed' : ''}`}
                >
                  <div className="jo-xyz-demo-progress-step__number">
                    {step > stepNumber ? '✓' : stepNumber}
                  </div>
                  <div className="jo-xyz-demo-progress-step__label">
                    {stepNumber === 1 && 'Property'}
                    {stepNumber === 2 && 'Terms'}
                    {stepNumber === 3 && 'Details'}
                  </div>
                </div>
              ))}
            </div>
            <div className="jo-xyz-demo-progress-bar">
              <div 
                className="jo-xyz-demo-progress-bar__fill"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          {/* Form Content */}
          <div className="jo-xyz-demo-list-asset-form">
            <div className="jo-xyz-demo-card">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </div>

            {/* Navigation */}
            <div className="jo-xyz-demo-list-asset-navigation">
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
                onClick={handleBack}
                disabled={step === 1}
              >
                Back
              </button>
              <button 
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                onClick={handleNext}
              >
                {step === 3 ? 'Create Listing' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListAsset;
